"use client"

import * as React from "react"
import { useTranslations } from "@/components/I18nProvider"
import { Input, Button } from "@sarkiai/ui"
import { Search, HelpCircle, MessageSquare, ArrowRight } from "lucide-react"
import FaqList from "@/components/home/FaqList"
import Link from "next/link"

export default function FAQPage() {
  const t = useTranslations("faq")
  const [searchQuery, setSearchQuery] = React.useState("")

  return (
    <main className="flex min-h-screen flex-col">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-black text-white overflow-hidden px-4 lg:px-12">
        <div className="absolute inset-0 bg-white/5 opacity-50" />
        <div className="container relative z-10 mx-auto text-center space-y-10 group">
          <div className="inline-flex items-center gap-2 px-6 py-2.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-xl shadow-black/20">
            <HelpCircle className="h-5 w-5" />
            <span className="font-black uppercase tracking-widest text-[10px]">{t("support_center") || "Support Center"}</span>
          </div>
          <h1 className="text-4xl md:text-8xl font-black tracking-tighter uppercase leading-none">
            {t("title") || "Foire aux questions"}
          </h1>
          <p className="text-xl md:text-2xl opacity-70 max-w-3xl mx-auto leading-relaxed">
            {t("subtitle") || "Trouvez des réponses rapides aux questions que vous vous posez sur nos services."}
          </p>

          <div className="max-w-2xl mx-auto relative group pt-4">
            <Search className="absolute left-6 top-1/2 md:translate-y-[-10%] h-6 w-6 text-white/40 group-focus-within:text-white transition-colors" />
            <Input
              type="text"
              placeholder={t("search_placeholder") || "Rechercher une réponse..."}
              className="h-16 pl-16 md:pl-16 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-[1.5rem] focus:border-white focus:ring-0 focus-visible:ring-0 text-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
      </section>

      {/* FAQ Content */}
      <section className="py-20 md:py-32 bg-background px-4 lg:px-12">
        <div className="container mx-auto">
          <FaqList showTabs showTitle={false} searchQuery={searchQuery} tabSize="md" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-black dark:bg-white text-white dark:text-black px-4 lg:px-12">
        <div className="container mx-auto text-center space-y-10 group">
          <div className="w-20 h-20 rounded-[2rem] bg-white dark:bg-black flex items-center justify-center mx-auto shadow-2xl shadow-black/20 group-hover:scale-110 transition-transform">
            <MessageSquare className="h-10 w-10 text-black dark:text-white" />
          </div>
          <div className="space-y-4">
            <h2 className="text-4xl md:text-7xl font-black uppercase tracking-tighter leading-none">
              {t("cta_title") || "Vous n'avez pas trouvé votre réponse ?"}
            </h2>
            <p className="text-xl md:text-2xl opacity-80 max-w-3xl mx-auto leading-relaxed">
              {t("cta_subtitle") || "Notre équipe est disponible pour vous accompagner et répondre à toutes vos interrogations spécifiques."}
            </p>
          </div>
          <div className="pt-6">
            <Button asChild size="lg" className="h-16 px-12 rounded-2xl bg-white dark:bg-black text-black dark:text-white font-black uppercase tracking-widest text-lg hover:scale-105 transition-transform shadow-2xl">
              <Link href="/contact">
                {t("cta_button") || "Contacter le support"}
                <ArrowRight className="ml-2 h-6 w-6" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  )
}
